import type { Header } from '@tanstack/react-table';
import { Filter } from './Filter';

export type HeaderFilterProps = {
  header: Header<any, unknown>;
};

/**
 * Resolves the filter value for display based on the filter variant.
 * Date-range filters need special handling because the URL stores string
 * representations while the DatePickerInput expects an array of
 * `string | null` values.
 */
const resolveFilterValue = (
  raw: unknown,
  variant: string | undefined,
): unknown => {
  if (variant === 'date-range') {
    if (!raw || (typeof raw === 'string' && raw === '')) {
      return [null, null];
    }

    // Ensure it's always an array (URL deserialization may return a plain string
    // when only one date segment exists without a comma separator).
    if (!Array.isArray(raw)) {
      return [raw];
    }

    return raw;
  }

  return raw ?? '';
};

export const HeaderFilterCell: React.FC<HeaderFilterProps> = ({ header }) => {
  const columnFilterValue = header.column.getFilterValue();
  const meta = header.column.columnDef.meta || {};

  const value = resolveFilterValue(columnFilterValue, meta.filterVariant);

  if (
    !header.column.getCanFilter() ||
    (!meta.filterVariant && !meta.filterComponent)
  ) {
    return null;
  }

  const filterProps = {
    type: meta.filterVariant,
    value,
    Component: meta.filterComponent,
    data: meta.filterSelectOptions,
    onChange: (event: any) => header.column.setFilterValue(event),
    filterProps: meta.filterProps,
  } as React.ComponentProps<typeof Filter>;

  return <Filter {...filterProps} />;
};
